import type { OrganizationProfile } from '@pulsify/shared'
import { type QueryClient, useQuery } from '@tanstack/react-query'
import { useSyncExternalStore } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { profilesApi } from '~/entities/profile/api/profiles'
import { pickActiveProfile } from '~/entities/profile/lib/active-profile'
import { type ProfileScope, profileScopeOf } from '~/entities/profile/lib/profile-scope'
import { needsProfileSetup } from '~/entities/profile/lib/profile-setup'
import { activeProfileStorageKey as storageKey } from '~/shared/lib/active-profile-store'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'
import { storageManager } from '~/shared/lib/storage-manager'
import type { Business } from '~/shared/types'

const listeners = new Set<() => void>()

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Switches the profile the dashboard works on, for this business on this device. */
export function setActiveProfileId(businessId: string, profileId: string): void {
  storageManager.set(storageKey(businessId), profileId)
  for (const listener of listeners) listener()
}

/**
 * The organization's profiles and the active one. `enabled` is false while profiles are switched
 * off on the server, and then the dashboard behaves as before.
 */
export function useOrganizationProfiles(businessId: string | null | undefined): {
  enabled: boolean
  profiles: OrganizationProfile[]
  active: OrganizationProfile | null
  storedId: string | null
  isLoading: boolean
} {
  const query = useQuery({
    queryKey: queryKey(QueryKey.ORGANIZATION_PROFILES, businessId),
    queryFn: () => profilesApi.list(businessId ?? ''),
    enabled: !!businessId,
    staleTime: 60_000,
  })
  const storedId = useSyncExternalStore(subscribe, () =>
    businessId ? storageManager.get<string>(storageKey(businessId)) : null,
  )
  const profiles = query.data?.profiles ?? []
  return {
    enabled: query.data?.enabled ?? false,
    profiles,
    active: pickActiveProfile(profiles, storedId),
    storedId,
    isLoading: query.isLoading,
  }
}

/** The active profile's id while profiles are on, to scope lists to it; undefined otherwise. */
export function useActiveProfileId(businessId: string | null | undefined): string | undefined {
  const { enabled, active } = useOrganizationProfiles(businessId)
  return enabled ? active?.id : undefined
}

/** What the dashboard works on for the current business; `isLoading` until that is known. */
export function useProfileScope(): {
  businessId: string | null
  enabled: boolean
  scope: ProfileScope
  isLoading: boolean
} {
  const business = useCurrentBusiness()
  const businessId = business.data?.id ?? null
  const { enabled, active, isLoading } = useOrganizationProfiles(businessId)
  return {
    businessId,
    enabled,
    scope: profileScopeOf(enabled, active),
    isLoading: business.isLoading || isLoading,
  }
}

/**
 * Whether the current business still has to set up its first profile; `isLoading` until that is
 * known. An admin viewing a business never has to.
 */
export function useProfileSetupState(): {
  business: Business | undefined
  needsSetup: boolean
  isLoading: boolean
} {
  const business = useCurrentBusiness()
  const { profiles, isLoading } = useOrganizationProfiles(business.data?.id)
  const adminViewing = storageManager.isAdmin() && !!storageManager.getAdminViewingBusinessId()
  return {
    business: business.data,
    needsSetup: needsProfileSetup(business.data, profiles.length, adminViewing),
    isLoading: business.isLoading || isLoading,
  }
}

/**
 * Finishes creating a profile: reloads the profiles, makes the new one active and drops every
 * cached answer of the previous one. Returns null when the new profile is not listed (yet).
 */
export async function activateNewProfile(
  queryClient: QueryClient,
  businessId: string,
  match: { listingId: string } | { profileId: string },
): Promise<OrganizationProfile | null> {
  const key = queryKey(QueryKey.ORGANIZATION_PROFILES, businessId)
  await queryClient.invalidateQueries({ queryKey: key })
  const data = await queryClient.fetchQuery({
    queryKey: key,
    queryFn: () => profilesApi.list(businessId),
  })
  const profile = data.profiles.find((candidate) =>
    'listingId' in match
      ? candidate.kind === 'place' && candidate.listingId === match.listingId
      : candidate.id === match.profileId,
  )
  if (!profile) return null
  setActiveProfileId(businessId, profile.id)
  return profile
}
